import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { configRows, originRows } from '@/features/debug/config-report'
import { probeBackend } from '@/features/debug/probe'
import { isRuntimeConfigLoaded } from '@/shared/config/runtime'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table'

/**
 * What this deployment thinks it is.
 *
 * Outside the shell and outside the guard, on purpose: the failure it exists to
 * explain -- a `PUBLIC_BACKEND_URL` pointing somewhere the browser cannot reach --
 * is the failure that makes signing in impossible, so a page requiring a session
 * would be unreachable exactly when it was wanted.
 *
 * Nothing here is secret. Every value on it is already served to every browser
 * that loads the application, in `/config.js`, before anybody has signed in.
 */
export function DebugPage() {
  const { t } = useTranslation()
  const origins = originRows()
  const backend = origins[0]?.url ?? ''

  const probe = useQuery({
    queryKey: ['debug', 'probe', backend],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: () => probeBackend(backend),
  })

  return (
    <div className="mx-auto w-full max-w-3xl space-y-8 p-6">
      <header>
        <h1 className="text-xl font-semibold">{t('debug.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('debug.why')}</p>
      </header>

      {!isRuntimeConfigLoaded() && (
        // The defaults make a missing config file work, against this page's own
        // origin -- so a single-domain deployment never notices, and every other
        // one is left setting variables that reach nothing.
        <Alert variant="destructive">
          <AlertTitle>{t('debug.noConfigTitle')}</AlertTitle>
          <AlertDescription>{t('debug.noConfigDescription')}</AlertDescription>
        </Alert>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium">{t('debug.reaching')}</h2>
          <Button
            variant="outline"
            size="sm"
            disabled={probe.isFetching}
            onClick={() => {
              void probe.refetch()
            }}
          >
            {t('action.retry')}
          </Button>
        </div>

        <dl className="divide-y rounded-lg border">
          {origins.map((origin) => (
            <div key={origin.key} className="flex items-baseline gap-4 p-3">
              <dt className="w-32 shrink-0 text-sm font-medium">
                {t(`debug.origin.${origin.key}`)}
              </dt>
              <dd className="min-w-0 flex-1 space-y-1">
                <p className="font-mono text-sm break-all">{origin.url}</p>
                {origin.fromPage && (
                  <p className="text-muted-foreground text-xs">
                    {t('debug.fromPageOrigin')}
                  </p>
                )}
              </dd>
            </div>
          ))}
        </dl>

        {probe.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : probe.data === undefined ? null : probe.data.ok ? (
          <Alert>
            <Check />
            <AlertTitle>
              {t('debug.answered', { milliseconds: probe.data.milliseconds })}
            </AlertTitle>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <X />
            <AlertTitle>
              {probe.data.status === null
                ? t('debug.noAnswer')
                : t('debug.refused', { status: probe.data.status })}
            </AlertTitle>
            <AlertDescription>
              {/* The browser's own message, verbatim. It is the only thing that
                  distinguishes a wrong hostname from a refused port, an expired
                  certificate or a proxy that is not passing `/api/` through. */}
              {probe.data.error ?? t('debug.refusedWhy')}
            </AlertDescription>
          </Alert>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-medium">{t('debug.configuration')}</h2>
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('debug.variable')}</TableHead>
                <TableHead>{t('debug.value')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configRows().map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="align-top font-mono text-xs whitespace-nowrap">
                    {row.variable}
                  </TableCell>
                  <TableCell className="align-top">
                    {row.isSet ? (
                      <span className="font-mono text-xs break-all whitespace-pre-wrap">
                        {row.value}
                      </span>
                    ) : (
                      <Badge variant="outline">{t('debug.notSet')}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-muted-foreground text-sm">{t('debug.settingsElsewhere')}</p>
      </section>
    </div>
  )
}
